import { useState, useEffect } from 'react';
import type { SessionManager } from '../lib/session';
import { isAuthenticatedState, type SessionState } from '../lib/session';

/**
 * Mirror a per-domain SessionManager's state machine into React so consumers
 * re-render on every transition, and expose the shared "is this session
 * currently active" predicate (Alive || Refreshing) applied to it.
 *
 * Both AuthProvider (user) and AdminAuthProvider (admin) use this instead of
 * hand-rolling the `useState(session.state)` + `onStateChange` subscription +
 * `isAuthenticatedState` derivation per domain (CU-868kykuzk). The subscription
 * and the predicate now live in one place, so the two domains cannot drift the
 * way they did when `isAuthenticated` was `=== Alive` on one side and included
 * `Refreshing` on the other (CU-868kyhnm9).
 *
 * Callers still AND `isSessionActive` with their domain's user/admin object to
 * derive `isAuthenticated` — the session being active is necessary but not
 * sufficient; the identity object must also be loaded.
 */
export function useSessionMirror(session: SessionManager): {
  sessionState: SessionState;
  isSessionActive: boolean;
} {
  const [sessionState, setSessionState] = useState<SessionState>(session.state);
  // onStateChange returns an unsubscribe fn, used as the effect cleanup. The
  // session is a stable module singleton, so this subscribes once per mount.
  useEffect(() => session.onStateChange(setSessionState), [session]);
  return { sessionState, isSessionActive: isAuthenticatedState(sessionState) };
}
